import { useEffect, useRef, useState } from "react";

const HOVER_RING_CLASS_NAMES = [
  "!border-[rgba(var(--ip-primary-glow),0.9)]",
  "!bg-[rgba(var(--ip-primary-glow),0.08)]",
];

export function Cursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    // 터치 디바이스에서는 커스텀 커서 비활성화
    if (window.matchMedia("(pointer: coarse)").matches) return;

    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");

    let mouseX = 0;
    let mouseY = 0;
    let ringX = 0;
    let ringY = 0;
    let raf: number;

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
    };

    const animate = () => {
      ringX += (mouseX - ringX) * 0.12;
      ringY += (mouseY - ringY) * 0.12;
      ring.style.transform = `translate(${ringX}px, ${ringY}px)`;
      raf = requestAnimationFrame(animate);
    };

    const onMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("a, button, [role='button'], .char-badge--link")) {
        dot.classList.add("!w-1.5", "!h-1.5", "!-top-[3px]", "!-left-[3px]");
        ring.classList.add("!w-16", "!h-16", "!-top-8", "!-left-8", ...HOVER_RING_CLASS_NAMES);
      }
    };

    const onMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("a, button, [role='button'], .char-badge--link")) {
        dot.classList.remove("!w-1.5", "!h-1.5", "!-top-[3px]", "!-left-[3px]");
        ring.classList.remove("!w-16", "!h-16", "!-top-8", "!-left-8", ...HOVER_RING_CLASS_NAMES);
      }
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("mouseout", onMouseOut);
    raf = requestAnimationFrame(animate);

    return () => {
      setEnabled(false);
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mouseout", onMouseOut);
      cancelAnimationFrame(raf);
    };
  }, []);

  const dotClassName =
    "bg-[var(--ip-primary)] shadow-[0_0_6px_rgba(var(--ip-primary-glow),0.8),0_0_14px_rgba(var(--ip-primary-glow),0.4)]";
  const ringClassName =
    "border-[rgba(var(--ip-primary-glow),0.5)] after:border-[rgba(255,255,255,0.2)]";

  return (
    <>
      <div
        ref={dotRef}
        aria-hidden="true"
        className={`cursor__dot fixed -top-[7px] -left-[7px] z-[9999] h-3.5 w-3.5 rounded-full pointer-events-none transition-[width,height,top,left] duration-200 ${dotClassName} ${enabled ? "" : "hidden"}`}
      />
      <div
        ref={ringRef}
        aria-hidden="true"
        className={`cursor__ring fixed -top-5 -left-5 z-[9998] h-10 w-10 rounded-full border pointer-events-none backdrop-blur-[1px] transition-[width,height,border-color,top,left,background] duration-300 after:absolute after:inset-[-4px] after:rounded-full after:border after:content-[''] ${ringClassName} ${enabled ? "" : "hidden"}`}
      />
    </>
  );
}
